import { useEffect, useState } from 'react';
import { initialsFor, type AvatarKind } from '@supporchat/shared';
import { api } from '../lib/api';

/**
 * Fotos já baixadas, por pessoa **e** por versão.
 *
 * A versão entra na chave porque é ela que diz que a foto mudou: sem isso, quem
 * troca o retrato continua aparecendo com o antigo até a aba ser fechada. Cada
 * `createObjectURL` sem revoke é memória retida, e a mesma foto aparece dezenas de
 * vezes na tela (lista, cabeçalho, cada bolha) — sem cache seriam dezenas de
 * downloads da mesma imagem.
 */
const cache = new Map<string, string>();

/**
 * Quem já falhou não é tentado de novo.
 *
 * A foto pode ter sido apagada por fora, ou o contato pode tê-la escondido entre
 * a listagem e o clique. Sem esta lista, cada re-render tentaria outra vez — um
 * 404 por bolha, em cada rolagem da conversa.
 */
const semFoto = new Set<string>();

export type AvatarSize = 'sm' | 'md' | 'lg';

interface Props {
  kind: AvatarKind;
  /** Id do contato ou do atendente; `null` quando nem isso se sabe. */
  id: string | null;
  name: string | null;
  /** `avatarUpdatedAt` — `null` significa "não tem foto", e vai direto às iniciais. */
  version: string | null;
  size?: AvatarSize;
  title?: string;
}

/**
 * Foto de perfil de um cliente ou de um atendente, com as iniciais como reserva.
 *
 * A reserva não é detalhe: boa parte dos contatos do WhatsApp esconde a foto de
 * quem não está na agenda deles, então as iniciais são a aparência normal de
 * metade da lista. Um quadrado cinza vazio deixaria a lista pior do que era antes
 * de existir foto alguma.
 *
 * O arquivo vem por `fetch` autenticado, e não por `src` apontando para a API:
 * o acesso exige o cabeçalho `Authorization`, e token em query string ficaria em
 * histórico, log de servidor e referer — a mesma decisão de `MediaAttachment`.
 */
export function Avatar({ kind, id, name, version, size = 'md', title }: Props) {
  const chave = id && version ? `${kind}:${id}:${version}` : null;
  const [url, setUrl] = useState<string | null>(() => (chave ? cache.get(chave) ?? null : null));

  useEffect(() => {
    // Sem chave é "esta pessoa não tem foto" — inclusive quando ela **tinha** e
    // acabou de remover: sem limpar aqui, o componente continuaria mostrando o
    // retrato que já não existe mais.
    if (!chave || !id || !version) {
      setUrl(null);
      return;
    }
    if (cache.has(chave)) {
      setUrl(cache.get(chave)!);
      return;
    }

    // Daqui para baixo não há foto para mostrar *ainda*. Zerar antes de sair vale
    // para os dois caminhos: a mesma instância do componente pode estar trocando
    // de pessoa (rolagem da lista), e a foto do anterior não pode ficar na tela.
    setUrl(null);
    if (semFoto.has(chave)) return;

    let ativo = true;

    void api
      .avatarObjectUrl(kind, id, version)
      .then((baixada) => {
        cache.set(chave, baixada);
        if (ativo) setUrl(baixada);
      })
      .catch(() => {
        semFoto.add(chave);
        // Silêncio de propósito: foto é identificação, não conteúdo do
        // atendimento. Um aviso de erro por linha da lista atrapalharia mais do
        // que a foto ajuda.
        if (ativo) setUrl(null);
      });

    return () => {
      ativo = false;
    };
  }, [chave, id, kind, version]);

  const rotulo = name?.trim() || 'sem nome';

  return (
    <span
      className={`avatar avatar--${size}${url ? ' avatar--foto' : ''}`}
      style={url ? undefined : { background: corDeFundo(id ?? rotulo) }}
      title={title ?? rotulo}
      // A foto é redundante: o nome está sempre escrito ao lado. Anunciá-la de
      // novo no leitor de tela só repetiria a mesma informação.
      aria-hidden="true"
    >
      {url ? <img className="avatar__img" src={url} alt="" /> : initialsFor(name)}
    </span>
  );
}

/**
 * Cor estável a partir do id: a mesma pessoa tem sempre a mesma cor.
 *
 * É o que faz as iniciais funcionarem como identificação em vez de decoração —
 * reconhecer "o círculo verde do Marcelo" na lista é mais rápido que ler o nome.
 * Saturação e luminosidade fixas mantêm o contraste do texto branco em qualquer
 * matiz sorteada.
 */
function corDeFundo(semente: string): string {
  let hash = 0;
  for (let i = 0; i < semente.length; i += 1) {
    hash = (hash * 31 + semente.charCodeAt(i)) % 360;
  }
  return `hsl(${hash} 42% 42%)`;
}
